import { toEpochMs } from './timeParse';

const pad2 = (n) => String(n).padStart(2, '0');
const pad3 = (n) => String(n).padStart(3, '0');

const getTimezoneOffsetHours = (timeZone, date) => {
  try {
    const utc = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tz = new Date(date.toLocaleString('en-US', { timeZone }));
    return (tz.getTime() - utc.getTime()) / 3600000;
  } catch {
    return 0;
  }
};

// Mirrors UIDateFormater's mode handling so CSV exports match what the UI
// displays. `timeZoneMode` is the global selection ('local' / 'project' /
// anything else → UTC). Returns the resolved IANA name and a header label.
export const resolveCsvTimeZone = (timeZoneMode, projectTimeZone) => {
  if (timeZoneMode === 'local') {
    let iana = 'UTC';
    try {
      iana = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      iana = 'UTC';
    }
    return { ianaTimeZone: iana, label: `${iana} (local)` };
  }
  if (timeZoneMode === 'project' && projectTimeZone) {
    return { ianaTimeZone: projectTimeZone, label: projectTimeZone };
  }
  return { ianaTimeZone: 'UTC', label: 'UTC' };
};

// Format a millisecond timestamp as "YYYY-MM-DD HH:MM:SS" in the given IANA
// timezone. Falls back to UTC if no timezone is provided or if formatting fails.
// Pass `{ includeMillis: true }` to append ".mmm" for millisecond resolution.
export const formatCsvTimestamp = (value, ianaTimeZone, options = {}) => {
  if (value == null || value === '') return '';
  // toEpochMs parses timezone-naive strings as UTC; Date.parse would read
  // them as browser-local and shift the exported instant (WEB-1218).
  const ms = toEpochMs(value);
  if (!Number.isFinite(ms)) return '';
  const base = new Date(ms);
  const target = ianaTimeZone && ianaTimeZone !== 'UTC'
    ? new Date(ms + getTimezoneOffsetHours(ianaTimeZone, base) * 3600000)
    : base;
  const millisSuffix = options.includeMillis ? `.${pad3(target.getUTCMilliseconds())}` : '';
  return `${target.getUTCFullYear()}-${pad2(target.getUTCMonth() + 1)}-${pad2(target.getUTCDate())} ${pad2(target.getUTCHours())}:${pad2(target.getUTCMinutes())}:${pad2(target.getUTCSeconds())}${millisSuffix}`;
};

const escapeCsvCell = (value) => {
  if (value == null) return '';
  const str = String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

export const buildCsv = (headers, rows) => {
  const headerLine = headers.map(escapeCsvCell).join(',');
  const bodyLines = rows.map((row) => row.map(escapeCsvCell).join(','));
  return [headerLine, ...bodyLines].join('\n');
};

export const downloadCsv = (filename, headers, rows, preambleLines = []) => {
  const body = buildCsv(headers, rows);
  const csv = preambleLines.length ? `${preambleLines.join('\n')}\n\n${body}` : body;
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
